"use client";

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { apiCall } from "@/utils/api";
import { routing } from "@/i18n/routing";

interface UserData {
  isLoggedIn: boolean;
  userId: string | null;
  credits: number;
  isAdmin: boolean;
  email?: string;
  userType?: string;
}

type Locale = (typeof routing.locales)[number];

interface UserContextType {
  isLoggedIn: boolean;
  isLoading: boolean;
  userId: string | null;
  credits: number;
  isAdmin: boolean;
  email?: string;
  updateCredits: (credits: number) => void;
  refreshUserData: () => void;
  logout: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

type UserProfileResponse = {
  status: string;
  email: string | null;
  user_id: number | null;
  user_type: string | null;
  amount_balance: number | null;
  balance?: number | null;
};

const fetchUserData = async (locale: Locale): Promise<UserData> => {
  const res = await apiCall<UserProfileResponse>("user/profile/", undefined, "GET", locale);
  const creditsValue =
    typeof res.amount_balance === "number"
      ? res.amount_balance
      : typeof res.balance === "number"
        ? res.balance
        : 0;

  const isLoggedIn = res.user_type === "user" || res.user_type === "staff";

  return {
    isLoggedIn,
    userId: res.user_id ? String(res.user_id) : null,
    credits: creditsValue,
    isAdmin: res.user_type === "staff",
    email: res.email ?? undefined,
    userType: res.user_type ?? undefined,
  };
};

export function UserProvider({ children }: { children: ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [credits, setCredits] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [email, setEmail] = useState<string | undefined>(undefined);
  const locale = routing.defaultLocale;

  const loadUserData = useCallback(async () => {
    setIsLoading(true);
    try {
      const userData = await fetchUserData(locale);
      setIsLoggedIn(userData.isLoggedIn);
      setUserId(userData.userId);
      setCredits(userData.credits);
      setIsAdmin(userData.isAdmin);
      setEmail(userData.email);
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      setIsLoggedIn(false);
      setUserId(null);
      setCredits(0);
      setIsAdmin(false);
      setEmail(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [locale]);

  // Load user data on startup
  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const refreshUserData = useCallback(() => {
    loadUserData();
  }, [loadUserData]);

  const updateCredits = useCallback((nextCredits: number) => {
    setCredits(nextCredits);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiCall("/user/session/", undefined, "DELETE", locale);
    } catch (error) {
      console.error("Failed to logout:", error);
    }

    sessionStorage.removeItem("intendedPath");
    setIsLoggedIn(false);
    setUserId(null);
    setCredits(0);
    setIsAdmin(false);
    setEmail(undefined);
  }, [locale]);

  return (
    <UserContext.Provider
      value={{
        isLoggedIn,
        isLoading,
        userId,
        credits,
        isAdmin,
        email,
        updateCredits,
        refreshUserData,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
